import React, { useEffect, useMemo, useRef, useState } from "react";
import type { DirectMessage, ServerMsg } from "@samra/shared";
import { del, get, post, send, subscribe } from "../api";
import { errText, getLang, t } from "../i18n";
import { toast, useStore, refreshDmUnread } from "../store";
import { startCall, useCall } from "../calls";
import { Avatar, Button, Empty, Link, Spinner, cx, useAsync } from "../ui";
import { ReportModal } from "./room";

interface Person {
  id: string;
  username: string;
  displayName: string;
  avatarUrl: string | null;
}

const timeOf = (iso: string) =>
  new Intl.DateTimeFormat(getLang() === "ar" ? "ar-SA" : "en-GB", { hour: "2-digit", minute: "2-digit" }).format(new Date(iso));

/** Messages page: a conversation list plus one open thread. Stacks on phones. */
export function Messages({ username }: { username?: string }) {
  const me = useStore((s) => s.me)!;
  const list = useAsync(() => get("/api/dm/threads"), []);
  const friends = useAsync(() => get("/api/friends"), []);
  useEffect(() => subscribe((m) => {
    if (m.t === "dm.msg") list.reload();
  }) as any, []);
  const threads: any[] = list.data?.threads ?? [];
  const friendList: any[] = friends.data?.friends ?? [];
  const others = friendList.filter((f) => !threads.some((th) => th.user.id === f.id));
  return (
    <div className={cx("messages", username && "has-thread")}>
      <aside className="msg-list panel" aria-label={t.messages}>
        <h2>{t.messagesTitle}</h2>
        {list.loading && !list.data && <Spinner />}
        {list.data && threads.length === 0 && <p className="muted">{t.noMessages}</p>}
        <ul className="list">
          {threads.map((th) => (
            <li key={th.user.id}>
              <Link to={`/messages/${th.user.username}`} className={cx("list-row thread-row", username === th.user.username && "is-active")}>
                <Avatar name={th.user.displayName} url={th.user.avatarUrl} size={40} />
                <span className="grow">
                  <strong>{th.user.displayName}</strong>
                  <small className="muted thread-preview">{th.last.mine ? `${t.yourMessage}: ` : ""}{th.last.text}</small>
                </span>
                {th.unread > 0 && <span className="badge" aria-label={`${th.unread} ${t.unreadBadge}`}>{th.unread > 9 ? "9+" : th.unread}</span>}
              </Link>
            </li>
          ))}
        </ul>
        {others.length > 0 && (
          <>
            <h3 className="muted">{t.friends}</h3>
            <ul className="list">
              {others.map((f) => (
                <li key={f.id}>
                  <Link to={`/messages/${f.username}`} className="list-row thread-row">
                    <Avatar name={f.displayName} url={f.avatarUrl} size={36} online={f.online} />
                    <span className="grow">{f.displayName} <small className="muted" dir="ltr">@{f.username}</small></span>
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}
        <p className="muted small">{t.messagesAndCalls}: <Link to="/settings">{t.settings}</Link></p>
      </aside>
      <section className="msg-thread panel">
        {username ? <Thread username={username} key={username} me={me.id} /> : <Empty>{t.noMessages}</Empty>}
      </section>
    </div>
  );
}

function Thread({ username, me }: { username: string; me: string }) {
  const [person, setPerson] = useState<Person | null>(null);
  const personRef = useRef<Person | null>(null);
  const [messages, setMessages] = useState<DirectMessage[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [loadError, setLoadError] = useState<any>(null);
  const [draft, setDraft] = useState("");
  const [typing, setTyping] = useState(false);
  const [reporting, setReporting] = useState<DirectMessage | null>(null);
  const [busy, setBusy] = useState(false);
  const online = useStore((s) => s.ws === "online");
  const typingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastTypingSent = useRef(0);
  const bottom = useRef<HTMLDivElement>(null);
  const seen = useMemo(() => new Set<string>(), [username]);
  const call = useCall();

  const load = async () => {
    try {
      const r = await get(`/api/dm/${encodeURIComponent(username)}`);
      personRef.current = r.user;
      setPerson(r.user);
      setBlocked(r.blocked);
      setHasMore(r.hasMore);
      seen.clear();
      r.messages.forEach((m: DirectMessage) => seen.add(m.id));
      setMessages(r.messages);
      await post(`/api/dm/${encodeURIComponent(username)}/read`).catch(() => {});
      void refreshDmUnread();
    } catch (e) {
      setLoadError(e);
    }
  };
  useEffect(() => {
    void load();
    return subscribe((m: ServerMsg) => {
      const cur = personRef.current;
      if (m.t === "dm.msg") {
        const x = m.msg;
        const isThisThread = !!cur && (x.senderId === cur.id || x.recipientId === cur.id);
        if (!isThisThread) return;
        if (seen.has(x.id)) return;
        seen.add(x.id);
        setMessages((list) => [...list, x]);
        if (x.senderId !== me) void post(`/api/dm/${encodeURIComponent(username)}/read`).catch(() => {});
      } else if (m.t === "dm.typing" && cur && m.from === cur.id) {
        setTyping(m.typing);
        if (typingTimer.current) clearTimeout(typingTimer.current);
        typingTimer.current = setTimeout(() => setTyping(false), 3000);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);
  useEffect(() => {
    bottom.current?.scrollIntoView({ block: "end" });
  }, [messages.length, typing]);

  const sendTyping = (value: string) => {
    setDraft(value);
    const now = Date.now();
    if (person && value && now - lastTypingSent.current > 2000) {
      lastTypingSent.current = now;
      // The server relays this only if the other person may receive messages from you.
      send({ t: "dm.typing", to: person.id, typing: true });
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || busy) return;
    setBusy(true);
    try {
      const r = await post(`/api/dm/${encodeURIComponent(username)}`, { text });
      setDraft("");
      if (!seen.has(r.message.id)) {
        seen.add(r.message.id);
        setMessages((list) => [...list, r.message]);
      }
    } catch (err) {
      toast(errText(err), "error");
    } finally {
      setBusy(false);
    }
  };

  const toggleBlock = async () => {
    if (!person) return;
    try {
      if (blocked) await del(`/api/blocks/${person.id}`);
      else await post("/api/blocks", { userId: person.id });
      setBlocked(!blocked);
      toast(blocked ? t.unblock : t.block);
    } catch (err) {
      toast(errText(err), "error");
    }
  };

  const callPeer = async () => {
    if (!person) return;
    const err = await startCall({ id: person.id, username: person.username, displayName: person.displayName, avatarUrl: person.avatarUrl });
    if (err) toast(errText({ code: err }), "error");
  };

  if (loadError) return <Empty>{errText(loadError)}</Empty>;
  if (!person) return <Spinner />;
  const callBusy = !!call && call.phase !== "ended";

  return (
    <div className="thread">
      <header className="thread-head">
        <Link to="/messages" className="thread-back">‹</Link>
        <Avatar name={person.displayName} url={person.avatarUrl} size={40} />
        <div className="grow">
          <Link to={`/u/${person.username}`}><strong>{person.displayName}</strong></Link>
          <small className="muted" dir="ltr"> @{person.username}</small>
          <div className="small muted" aria-live="polite">{typing ? t.typingNow : online ? t.realtimeOnline : t.realtimeOffline}</div>
        </div>
        {!blocked && (
          <Button kind="ghost" onClick={callPeer} disabled={callBusy} aria-label={t.messageCall} title={t.messageCall}>📞</Button>
        )}
        <Button kind={blocked ? "ghost" : "danger"} onClick={toggleBlock}>{blocked ? t.unblock : t.block}</Button>
      </header>
      {blocked && <p className="notice">{t.blockedChat}</p>}
      <div className="thread-body" role="log" aria-live="polite">
        {hasMore && (
          <Button kind="quiet" onClick={async () => {
            const oldest = messages[0]?.at;
            if (!oldest) return;
            const r = await get(`/api/dm/${encodeURIComponent(username)}?before=${encodeURIComponent(oldest)}`);
            setHasMore(r.hasMore);
            setMessages((list) => [...r.messages.filter((m: DirectMessage) => !seen.has(m.id)).map((m: DirectMessage) => (seen.add(m.id), m)), ...list]);
          }}>{t.loadOlder}</Button>
        )}
        {messages.map((m) => {
          const mine = m.senderId === me;
          return (
            <div key={m.id} className={cx("bubble", mine ? "bubble-mine" : "bubble-theirs")}>
              <p>{m.text}</p>
              <small className="muted">
                <time dateTime={m.at}>{timeOf(m.at)}</time>
                {mine && m.readAt && <> · {t.seenAt}</>}
                {!mine && <button type="button" className="link-btn" onClick={() => setReporting(m)}>{t.reportMessage}</button>}
              </small>
            </div>
          );
        })}
        <div ref={bottom} />
      </div>
      <form className="composer" onSubmit={submit}>
        <textarea
          value={draft}
          onChange={(e) => sendTyping(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void submit(e as any);
            }
          }}
          maxLength={300}
          rows={2}
          placeholder={t.writeMessage}
          aria-label={t.writeMessage}
          disabled={blocked}
        />
        <Button type="submit" busy={busy} disabled={blocked || !draft.trim()}>{t.sendMessage}</Button>
        <small className="muted composer-hint">{t.messageInputHint}</small>
      </form>
      {reporting && (
        <ReportModal userId={reporting.senderId} name={person.displayName} dmMessageId={reporting.id} onClose={() => setReporting(null)} />
      )}
    </div>
  );
}

